import { ReceiptText, FileText, Landmark, SearchCheck, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const SERVICES = [
  {
    id: "gst-compliance",
    icon: ReceiptText,
    title: "GST Compliance & Filings",
    desc: "Registration, monthly and annual returns, reconciliations, e-invoicing and departmental notices — handled before deadlines become penalties.",
    tags: ["GSTR-1 / 3B / 9", "Registration", "Notices"],
    wide: true,
  },
  {
    id: "income-tax",
    icon: FileText,
    title: "Income Tax Advisory & Filings",
    desc: "ITR for individuals and businesses, TDS management, tax planning and assessment representation.",
    tags: ["ITR Filing", "TDS", "Planning"],
  },
  {
    id: "corporate-law",
    icon: Landmark,
    title: "Corporate Law & MCA Filings",
    desc: "Company and LLP registration, ROC annual filings, secretarial and statutory compliance.",
    tags: ["Incorporation", "ROC", "LLP"],
  },
  {
    id: "auditing",
    icon: SearchCheck,
    title: "Statutory & Internal Auditing",
    desc: "Tax audits, internal financial control audits, stock audits and independent verification for banks.",
    tags: ["Tax Audit", "Stock Audit", "Internal Audit"],
  },
];

export const Services = () => (
  <section id="services" className="py-16 sm:py-24" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-brass-dark font-semibold mb-4">What we handle</p>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ink max-w-2xl">
          Four practices. One accountable firm.
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className={s.wide ? "lg:col-span-2" : ""}>
            <article
              data-testid={`service-card-${s.id}`}
              className="group h-full rounded-3xl bg-white border border-slate-200 p-8 flex flex-col transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.25)] hover:border-brass/50"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brass-light text-brass-dark">
                  <s.icon size={22} strokeWidth={1.8} />
                </span>
                <span className="font-mono text-xs text-ink-muted">0{i + 1}</span>
              </div>
              <h3 className="mt-6 font-serif text-xl sm:text-2xl font-medium text-ink">{s.title}</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-ink-muted flex-1">{s.desc}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span key={t} className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 text-ink-soft border border-slate-200">{t}</span>
                ))}
              </div>
              <a href="#contact" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brass-dark opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                Enquire now <ArrowUpRight size={15} />
              </a>
            </article>
          </Reveal>
        ))}
        <Reveal delay={0.32}>
          <a href="#contact" data-testid="services-cta-card"
            className="group h-full min-h-[220px] rounded-3xl bg-forest text-white p-8 flex flex-col justify-between transition-transform duration-500 hover:-translate-y-1.5">
            <p className="font-serif text-2xl leading-snug">Not sure which service fits?<br /><span className="italic text-brass">Ask us directly.</span></p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold mt-6 group-hover:gap-3 transition-all duration-300">
              Get a free consultation <ArrowUpRight size={16} />
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
